'use client';

import { type ReactEventHandler, useState } from 'react';
import { FederatedPhoto, type FederatedPhotoProps } from './index';
import { buildFederatedPhotoAttrs, type CcFtSize } from './federatedPhoto';

export type FederatedPhotoWithFallbackProps = FederatedPhotoProps & {
  fallback?: 'placeholder' | 'none';
};

const DEFAULT_SIZE: CcFtSize = 768;

/**
 * Client-only FederatedPhoto wrapper for image surfaces where a broken Fed
 * Photos render is worse than the added hydration cost.
 *
 * Keep the default `FederatedPhoto` server-compatible for static / server
 * render paths. Use this wrapper only on already-interactive surfaces, or on
 * important listing-photo surfaces where recovering from transient Federated Photo
 * render failures is worth introducing a client boundary.
 *
 * On image-load failure, this swaps the rendered source to the 192w
 * placeholder render for the same photo key. The wrapper then asks
 * `FederatedPhoto` to preserve that fallback URL so we don't keep
 * advertising the failed larger srcset, and so a placeholder failure cannot
 * loop.
 *
 * Caller-provided `onError` runs before the fallback so analytics / logging
 * still see the original failed URL. Pass `fallback="none"` when a caller
 * needs to observe or handle broken images itself.
 */
export function FederatedPhotoWithFallback({
  fallback = 'placeholder',
  src,
  defaultSize = DEFAULT_SIZE,
  treatment,
  preserveSource = false,
  onError,
  ...imageProps
}: FederatedPhotoWithFallbackProps) {
  const [hasError, setHasError] = useState(false);
  const responsiveAttrs =
    preserveSource || fallback === 'none'
      ? null
      : buildFederatedPhotoAttrs(src, defaultSize, [defaultSize], { treatment });
  const fallbackSrc = responsiveAttrs?.placeholder;
  const fallbackActive = hasError && fallbackSrc !== undefined;
  const imageSrc = fallbackActive ? fallbackSrc : src;

  const handleError: ReactEventHandler<HTMLImageElement> = (event) => {
    onError?.(event);
    if (fallbackSrc && !hasError) {
      setHasError(true);
    }
  };

  return (
    <FederatedPhoto
      {...imageProps}
      src={imageSrc}
      defaultSize={defaultSize}
      treatment={treatment}
      preserveSource={fallbackActive || preserveSource}
      onError={handleError}
    />
  );
}
